import { useTranslation } from 'next-i18next';
import cx from 'classnames';
import { Link, Text } from '@dx-ui/global-ui-prism';

import Icon from '@dx-ui/osc-icon';
import { HEADER_CLICK_IDS } from '../../types';
import type { HeaderProps, TDrawerLink } from '../../types';
import {
  dispatchGlobalNavigationClick,
  dispatchGlobalNavigationClickBasedOnLabel,
} from '../../header.utilities';
import { getLocaleFormattedNumber, replaceLang } from '@dx-ui/framework-i18n';
import { useRouter } from 'next/router';
import { type ComponentProps, type ReactNode } from 'react';
import { useHeaderContext } from '../../context/header-context';

type DesktopNavigationPopupContentsProps = {
  subMenu: TDrawerLink[];
  label: string;
  className?: string;
};

export const DesktopNavigationPopupContents = ({
  subMenu,
  label,
  className,
}: DesktopNavigationPopupContentsProps) => {
  const { locale = 'en' } = useRouter();
  const {
    navigation: { onGlobalNavigationClick, isLegacyGlobalNavigationClickAnalyticsEnabled = true },
  } = useHeaderContext();

  return (
    <div className={cx('container flex-1 py-5', className)}>
      <DesktopMenuUserList>
        {subMenu.map(
          ({ label: subMenuLabel, link: { experimentationConfiguration, ...link }, onClick }) => (
            <DesktopMenuUserLinkListItem key={subMenuLabel}>
              <Link
                colorTheme="inherit"
                {...link}
                url={replaceLang(link?.url, locale)}
                data-conductrics-goal={experimentationConfiguration?.goal}
                data-conductrics-value={experimentationConfiguration?.value}
                underline={false}
                className="osc-nav-drawer-link-text"
                anchorClassName="osc-nav-drawer-link p-3 brand-ey:focus:font-normal block"
                onClick={(event) => {
                  dispatchGlobalNavigationClickBasedOnLabel(
                    subMenuLabel,
                    label,
                    onGlobalNavigationClick,
                    isLegacyGlobalNavigationClickAnalyticsEnabled
                  );
                  onClick?.();
                  link?.onClick?.(event);
                }}
              >
                {subMenuLabel}
              </Link>
            </DesktopMenuUserLinkListItem>
          )
        )}
      </DesktopMenuUserList>
    </div>
  );
};

export type DesktopUserPopupContentsProps = Pick<
  HeaderProps,
  | 'userLinks'
  | 'shouldOpenAccountLinksInNewWindow'
  | 'shouldSuppressHonorsAccountLinks'
  | 'onSignOut'
> & {
  user: Exclude<HeaderProps['user'], undefined>;
};
export const DesktopUserPopupContents = ({
  user,
  userLinks,
  shouldOpenAccountLinksInNewWindow,
  shouldSuppressHonorsAccountLinks,
  onSignOut,
}: DesktopUserPopupContentsProps) => {
  const { locale = 'en' } = useRouter();
  const [t] = useTranslation('osc-header');
  const {
    navigation: { onGlobalNavigationClick, isLegacyGlobalNavigationClickAnalyticsEnabled = true },
  } = useHeaderContext();

  const formattedHonorsPointsTotal = getLocaleFormattedNumber(user?.honorsPoints, { locale });

  return (
    <div className="container flex py-5">
      <div className="flex items-center">
        <div className="flex size-20 shrink-0 items-center justify-center rounded-full bg-points">
          <Icon name="user" size="4xl" />
        </div>
        <div className="ms-5">
          <Text size="sm" className="mb-1 font-bold brand-ey:font-normal">
            {t('greetings', { username: user.name })}
          </Text>
          <Text className="mb-1 capitalize">
            <Text as="span" size="sm" className="font-bold underline brand-ey:font-normal">
              {user.honorsTier}
            </Text>{' '}
            {t('status')}
          </Text>
          <Text size="sm" className="mb-1 brand-ey:font-normal">
            {t('honorsPointsTotal', { honorsPointsTotal: formattedHonorsPointsTotal })}
          </Text>
          {user.hhonorsNumber ? (
            <Text size="sm" className="mb-1 brand-ey:font-normal">
              {t('hiltonHonorsNumber', { hhonorsNumber: user.hhonorsNumber })}
            </Text>
          ) : null}
        </div>
      </div>
      <div className="ms-5 flex-1 xl:ms-10">
        <DesktopMenuUserList>
          {userLinks?.accountLink ? (
            <DesktopUserLink
              url={userLinks?.accountLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameHiltonHonorsHome,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('hiltonHonorsHome')}
            </DesktopUserLink>
          ) : null}
          {userLinks?.honorsActivityLink && !shouldSuppressHonorsAccountLinks ? (
            <DesktopUserLink
              url={userLinks?.honorsActivityLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                    ? HEADER_CLICK_IDS.MemberNameHiltonHonorsActivity
                    : HEADER_CLICK_IDS.MemberNameActivity,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('activity')}
            </DesktopUserLink>
          ) : null}

          {userLinks?.honorsPointsLink?.url && !shouldSuppressHonorsAccountLinks ? (
            <DesktopUserLink
              url={userLinks.honorsPointsLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                    ? HEADER_CLICK_IDS.MemberNameHiltonHonorsPoints
                    : HEADER_CLICK_IDS.MemberNamePoints,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('pointsLabel')}
            </DesktopUserLink>
          ) : null}

          {userLinks?.honorsProfileLink?.url && !shouldSuppressHonorsAccountLinks ? (
            <DesktopUserLink
              url={userLinks.honorsProfileLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                    ? HEADER_CLICK_IDS.MemberNameHiltonHonorsProfile
                    : HEADER_CLICK_IDS.MemberNameProfile,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('profile')}
            </DesktopUserLink>
          ) : null}

          {userLinks?.yourOffersLink?.url && !shouldSuppressHonorsAccountLinks ? (
            <DesktopUserLink
              url={userLinks.yourOffersLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameHiltonHonorsYourOffers,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('yourOffers')}
            </DesktopUserLink>
          ) : null}

          {userLinks?.goHiltonLink?.url ? (
            <DesktopUserLink
              url={userLinks.goHiltonLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameGoHiltonProgramDetails,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('goHiltonProgramDetails')}
            </DesktopUserLink>
          ) : null}

          {userLinks?.friendsAndFamilyLink?.url ? (
            <DesktopUserLink
              url={userLinks.friendsAndFamilyLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameManageFriendsAndFamily,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('manageFriendsAndFamily')}
            </DesktopUserLink>
          ) : null}

          {userLinks?.hgvMaxLink?.url ? (
            <DesktopUserLink
              url={userLinks.hgvMaxLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameHgvMaxRateDetails,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('hgvMaxRateDetails')}
            </DesktopUserLink>
          ) : null}

          {userLinks?.b2bMemberLink?.url ? (
            <DesktopUserLink
              url={userLinks.b2bMemberLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameManageHiltonForBusiness,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {userLinks?.b2bMemberLink?.label}
            </DesktopUserLink>
          ) : null}
          <DesktopMenuUserLinkListItem>
            <button
              className="osc-nav-drawer-link osc-nav-drawer-link-text size-full whitespace-nowrap py-3 text-start"
              onClick={async () => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameSignOut,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
                await onSignOut();
              }}
              type="button"
            >
              {t('signOut')}
            </button>
          </DesktopMenuUserLinkListItem>
        </DesktopMenuUserList>
      </div>
    </div>
  );
};

const DesktopMenuUserList = ({ children, ...listProps }: ComponentProps<'ul'>) => {
  return (
    <ul {...listProps} className={cx('grid grid-cols-3 gap-x-6', listProps.className)}>
      {children}
    </ul>
  );
};

const DesktopMenuUserLinkListItem = ({ children, ...listItemProps }: ComponentProps<'li'>) => {
  return (
    <li {...listItemProps} data-testid="menu-list-item" className="py-3">
      <span className="block w-full border-b-2 border-surface hover:font-bold">{children}</span>
    </li>
  );
};

const DesktopUserLink = ({
  url,
  onClick,
  children,
  isNewWindow,
  anchorClassName,
}: {
  url: string;
  onClick: () => void;
  children: ReactNode;
  isNewWindow?: boolean;
  anchorClassName?: string;
}) => {
  return (
    <DesktopMenuUserLinkListItem>
      <Link
        colorTheme="inherit"
        url={url}
        underline={false}
        className="osc-nav-drawer-link-text whitespace-nowrap"
        anchorClassName={cx('osc-nav-drawer-link block size-full py-3', anchorClassName)}
        onClick={onClick}
        isNewWindow={isNewWindow}
      >
        {children}
      </Link>
    </DesktopMenuUserLinkListItem>
  );
};
